import { Box, IconButton, Link, MenuItem, Paper, Select, Table, TableBody, TableCell, TableHead, TablePagination, TableRow } from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import SearchTextField from '../../components/SearchTextField';
import TableSkeleton from '../../components/TableSkeleton';
import { useTransferMonthlyWithSearchKey } from '../../hooks/queries/useTransfer';
import { useQueryStringState } from '../../hooks/useQueryStringState';
import { TransferSearchType } from '../../services/api/Transfer';
import { ITransfer } from '../../services/types';
import useStyles from './TransferListStyles';
import TransferMonthlyStories from './TransferMonthlyStories';

export const ROW_PER_PAGE = 500;

interface IProps {
  itemCountChanger: React.Dispatch<React.SetStateAction<number>>;
  totalCountChanger: React.Dispatch<React.SetStateAction<number>>;
  monthStateChanger: React.Dispatch<React.SetStateAction<string>>;
  pageStateChanger: React.Dispatch<React.SetStateAction<number>>;
  searchTypeChanger: React.Dispatch<React.SetStateAction<TransferSearchType>>;
  searchKeywordChanger: React.Dispatch<React.SetStateAction<string | undefined>>;
}

const TransferMonthlyList = (props: IProps) => {
  const now = moment();

  const classes = useStyles();

  const [selectedYear, setSelectedYear] = useState<string>(now.format('YYYY'));
  const [selectedMonth, setSelectedMonth] = useState<string>(now.add(-1, 'M').format('MM'));

  const [page, setPage] = useState(0);

  const [unfoldedTransferIds, setUnfoldedTransferIds] = useState<number[]>([]);

  const [searchType, setSearchType] = useQueryStringState<TransferSearchType>('monthlySearchType', TransferSearchType.StoryId);
  const [searchKey, setSearchKey] = useQueryStringState<string>('monthlySearchKey');

  const { data, isLoading } = useTransferMonthlyWithSearchKey(Number(`${selectedYear}${selectedMonth}`), page + 1, ROW_PER_PAGE, {
    searchType: searchKey ? searchType : undefined,
    searchKey: searchKey ? Number(searchKey) : undefined,
  });

  const handleToggleClick = (id: number) => {
    if (unfoldedTransferIds.indexOf(id) >= 0) {
      setUnfoldedTransferIds(unfoldedTransferIds.filter(item => item !== id));
    } else {
      setUnfoldedTransferIds(unfoldedTransferIds.concat(id));
    }
  };

  const handleYearChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedYear(String(event.target.value));
    setPage(0);
  };

  const handleMonthChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedMonth(String(event.target.value));
    setPage(0);
  };

  const handleSearchTypeChange = (e: React.ChangeEvent<any>) => {
    setSearchType(e.target.value as TransferSearchType);
    setPage(0);
  };

  const handleSearchKeySubmit = (value: string) => {
    setSearchKey(value);
    setPage(0);
    setUnfoldedTransferIds([]);
  };

  const handleSearchKeyCancel = () => {
    setSearchKey(undefined);
    setPage(0);
    setUnfoldedTransferIds([]);
  };

  const monthOptions = useMemo((): JSX.Element[] => {
    const startMoment = moment().startOf('year');
    const endMoment = moment().endOf('year');
    const options = [];

    while (startMoment.isSameOrBefore(endMoment)) {
      options.push(
        <MenuItem key={`options-month-${startMoment.format('MMM')}`} value={startMoment.format('MM')}>
          {startMoment.format('MMM')}
        </MenuItem>,
      );
      startMoment.add(1, 'month');
    }

    return options;
  }, []);

  const yearOptions = useMemo((): JSX.Element[] => {
    const nowMoment = moment();
    const options = [];

    while (nowMoment.year() > 2015) {
      const year = nowMoment.format('YYYY');
      options.push(
        <MenuItem key={`options-year-${year}`} value={nowMoment.format('YYYY')}>
          {nowMoment.format('YYYY')}
        </MenuItem>,
      );
      nowMoment.add(-1, 'y');
    }

    return options;
  }, []);

  useEffect(() => {
    props.monthStateChanger(`${selectedYear}${selectedMonth}`);
  }, [props, selectedYear, selectedMonth]);

  useEffect(() => {
    props.searchTypeChanger(searchType);
    props.searchKeywordChanger(searchKey);
  }, [props, searchKey, searchType]);

  useEffect(() => {
    props.pageStateChanger(page);
  }, [props, page]);

  useEffect(() => {
    props.totalCountChanger(data?.totalCount || 0);
  }, [props, data?.totalCount]);

  useEffect(() => {
    if (data?.hasMore) {
      props.itemCountChanger(ROW_PER_PAGE);
    } else {
      props.itemCountChanger(data?.items.length || 0);
    }
  }, [props, data?.items, data?.hasMore, page]);

  return (
    <>
      <Box marginBottom={'20px'} style={{ display: 'flex' }}>
        <Select value={selectedYear} onChange={handleYearChange}>
          {yearOptions}
        </Select>
        <Select value={selectedMonth} onChange={handleMonthChange}>
          {monthOptions}
        </Select>

        <Box style={{ marginLeft: 'auto' }}>
          <Select value={searchType} onChange={handleSearchTypeChange}>
            <MenuItem value={TransferSearchType.StoryId}>Story Id</MenuItem>
            <MenuItem value={TransferSearchType.SeasonId}>Season Id</MenuItem>
          </Select>
          <SearchTextField
            type={'number'}
            defaultValue={searchKey}
            onValueSubmit={handleSearchKeySubmit}
            onCancel={handleSearchKeyCancel}
            isSearching={isLoading}
          />
        </Box>
      </Box>
      <Paper>
        <Box display="flex" alignItems="center" paddingTop={2} paddingLeft={2} paddingRight={2}>
          <Box flex="1">
            <span className={classes.sortLabel}>Total: </span>
            <span className={classes.countValue}>{data?.totalCount ? data.totalCount : 0}</span>
          </Box>
        </Box>
        <Table component={'table'}>
          <TableHead component={'thead'} className={classes.headerRow}>
            <TableRow component={'tr'}>
              <TableCell component={'td'} align="center" style={{ width: 30 }}></TableCell>
              <TableCell component={'td'} align="left" style={{ width: 30 }}>
                No.
              </TableCell>
              <TableCell component={'td'} align="left">
                Writer (Payee)
              </TableCell>
              <TableCell component={'td'} align="center" style={{ width: 157 }}></TableCell>
              <TableCell component={'td'} align="right">
                Coins Redeemed
              </TableCell>
              <TableCell component={'td'} align="right">
                Total Payable
              </TableCell>
              <TableCell component={'td'} align="center"></TableCell>
              <TableCell component={'td'}></TableCell>
              <TableCell component={'td'}></TableCell>
              <TableCell component={'td'}></TableCell>
              <TableCell component={'td'}></TableCell>
              <TableCell component={'td'}></TableCell>
              <TableCell component={'td'}></TableCell>
              <TableCell component={'td'}></TableCell>
              <TableCell component={'td'}></TableCell>
            </TableRow>
          </TableHead>
          {isLoading && (
            <TableBody component={'tbody'}>
              <TableSkeleton rows={10} columns={15} isOriginalDefaultComponent={true} />
            </TableBody>
          )}
          {!isLoading &&
            data &&
            data.items.map((transfer: ITransfer, no: number) => {
              const { writer, transferredCoins, transferredAmount } = transfer;
              const restNumber = no + page * ROW_PER_PAGE;
              const isUnfolded = unfoldedTransferIds.indexOf(restNumber) >= 0;

              return (
                <React.Fragment key={`transfer-monthly-row-${restNumber}`}>
                  <TableBody component={'tbody'} className={isUnfolded ? classes.selectedRow : ''}>
                    <TableRow component={'tr'} hover className={classes.tableRow}>
                      <TableCell component={'td'} align="center">
                        <IconButton size="small" onClick={() => handleToggleClick(restNumber)}>
                          {isUnfolded && <KeyboardArrowUpIcon />}
                          {!isUnfolded && <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell component={'td'} align="left">
                        {(data.totalCount || 0) - restNumber}
                      </TableCell>
                      <TableCell component={'td'} align="left">
                        <Link href={`${process.env.CREW_DEFAULT_URI}/users/${writer.id}`} target="_blank">
                          {writer.username}
                        </Link>
                      </TableCell>
                      <TableCell component={'td'} align="center"></TableCell>
                      <TableCell component={'td'} align="right">
                        {Number(transferredCoins).toLocaleString()}
                      </TableCell>
                      <TableCell component={'td'} align="right">
                        $ {Number(transferredAmount).toLocaleString('en-US', { maximumFractionDigits: 4 })}
                      </TableCell>
                      <TableCell component={'td'} align="left"></TableCell>
                      <TableCell component={'td'}></TableCell>
                      <TableCell component={'td'}></TableCell>
                      <TableCell component={'td'}></TableCell>
                      <TableCell component={'td'}></TableCell>
                      <TableCell component={'td'}></TableCell>
                      <TableCell component={'td'}></TableCell>
                      <TableCell component={'td'}></TableCell>
                      <TableCell component={'td'}></TableCell>
                    </TableRow>
                  </TableBody>
                  {isUnfolded && (
                    <TransferMonthlyStories
                      key={`transfer-monthly-stories-${restNumber}`}
                      transfersByStory={transfer.transfersByStory}
                      mgReports={transfer.mgReports}
                    />
                  )}
                </React.Fragment>
              );
            })}
          {!isLoading && (!data || data.totalCount === 0) && (
            <TableBody component={'tbody'}>
              <TableRow component={'tr'}>
                <TableCell component={'td'} colSpan={15} align="center">
                  This Story(or Season) does not exist. Please Try Again.
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
        {data?.totalCount !== undefined && (
          <TablePagination
            count={data.totalCount}
            rowsPerPage={ROW_PER_PAGE}
            page={page}
            rowsPerPageOptions={[ROW_PER_PAGE]}
            onChangePage={(e: unknown, v: number) => {
              setPage(v);
            }}
          />
        )}
      </Paper>
    </>
  );
};

export default TransferMonthlyList;
